import {
	ASSET_TYPE_COLOR_PALETTE,
	ASSET_TYPE_ICONS,
	COLLECTION_ASSET_TYPE_ICON,
	DEFAULT_ASSET_TYPE_COLOR,
	IkeToken,
} from '@ikegps/core-api';
import { FormModelFieldAssetType } from '@ikegps/core-shared';
import { action, makeObservable, observable } from 'mobx';
import { ImageButton } from '../../../components/button/image.button';
import { AssetTypeSwatch } from '../../../components/asset.type.swatch';
import { Icon } from '../../../components/icon';
import { MaterialIcon } from '../../../components/icon/icons';
import { Input } from '../../../components/input/input';
import { type InputState } from '../../../components/input/input.state';
import { ProFeatureTooltip } from '../../../components/pro.feature.tooltip';
import { SectionHeader } from '../../../components/section.header/section.header';
import { addStyles, DefaultTheme } from '../../../components/theme';
import { T, Translate } from '../../../i18n';
import { m, type MChild, type MithrilVNode } from '../../../mithril';
import { Services } from '../../../services';
import { classNames } from '../../../util/classnames';
import { AnchoredPopover } from '../../../components/anchored.popover/anchored.popover';
import { CSS as PopoverCSS } from '../../../components/anchored.popover/anchored.popover.style';
import { AssetTypeCustomizer } from '../../asset.type.customizer/asset.type.customizer';
import { POPOVER_WIDTH } from '../../asset.type.customizer/asset.type.customizer.style';
import { FormDesigner } from '../form.designer';
import { FormFieldEditor } from './form.field.editor';

export const CSS = {
	container: 'c-FdcAssetType',
	rows: 'c-FdcAssetType__rows',
	header: 'c-FdcAssetType__header',
	section: 'c-FdcAssetType__section',
	rowGroup: 'c-FdcAssetType__rowGroup',
	row: 'c-FdcAssetType__row',
	text: 'c-FdcAssetType__text',
	textLabel: 'c-FdcAssetType__textLabel',
	symbol: 'c-FdcAssetType__symbol',
	symbolButton: 'c-FdcAssetType__symbolButton',
	symbolSlot: 'c-FdcAssetType__symbolSlot',
	symbolEdit: 'c-FdcAssetType__symbolEdit',
	visible: 'c-FdcAssetType__visible',
	visibleIcon: 'c-FdcAssetType__visibleIcon',
	actions: 'c-FdcAssetType__actions',
	hidden: 'c-FdcAssetType__hidden',
	ghost: 'c-FdcAssetType__ghost',
	error: 'c-FdcAssetType__error',
	disabled: 'c-FdcAssetType__disabled',
};

// symbol = pencil slot (16) + gap (4) + swatch (48) + gap (4) + spacer slot (16)
const COL = { symbol: '88px', visible: '40px', actions: '40px' };

// The Collection preset's icon is reserved for it alone — a custom row's picker never offers it, so a custom
// type can't be mistaken for (or collide visually with) the seeded Collection default. The grid only ever
// renders for a custom row anyway (see `viewSymbolCustomizer`'s `iconEditable: !isPreset`), so this filtering
// never affects a preset's own (fixed, non-editable) icon.
const CUSTOM_ASSET_TYPE_ICONS = ASSET_TYPE_ICONS.filter((icon) => icon.id !== COLLECTION_ASSET_TYPE_ICON);

// Sentinel marking the name's position in the localized heading, so we can split around it and only ellipsize the name.
const NAME_SLOT = '\u0000';

addStyles({
	[CSS.container]: {
		width: '100%',
		'max-width': '580px',
		display: 'flex',
		'flex-direction': 'column',
		padding: DefaultTheme.space(2),
	},
	// The variable-length row list is isolated from the popover sibling so inserting a row never shifts it.
	[CSS.rows]: { display: 'flex', 'flex-direction': 'column' },
	[CSS.header]: {
		display: 'flex',
		'align-items': 'center',
		gap: DefaultTheme.space(1),
		'font-weight': 'bold',
		color: DefaultTheme.font.color.supporting,
		'margin-bottom': DefaultTheme.space(1),
	},
	// Each section's rows live in their own container so list diffing stays isolated per section.
	[CSS.section]: { display: 'flex', 'flex-direction': 'column' },
	// Each row + its inline error are one group, so the divider sits below the error, not above it.
	[CSS.rowGroup]: { 'border-bottom': `1px solid ${DefaultTheme.font.color.divider}` },
	[CSS.row]: {
		display: 'flex',
		'align-items': 'center',
		gap: DefaultTheme.space(1),
		'min-height': '32px',
		padding: '2px 0',
	},
	// 32px cells (matching the select-list editor) keep preset labels and custom inputs on one rhythm.
	[CSS.text]: { flex: '1 1 auto', 'min-width': '120px', height: '32px', display: 'flex', 'align-items': 'center' },
	[CSS.textLabel]: { overflow: 'hidden', 'text-overflow': 'ellipsis', 'white-space': 'nowrap' },
	[CSS.symbol]: { width: COL.symbol, display: 'flex', 'align-items': 'center', 'justify-content': 'center' },
	[CSS.symbolButton]: {
		display: 'flex',
		'align-items': 'center',
		'justify-content': 'center',
		gap: '4px',
		width: '100%',
		cursor: 'pointer',
		background: 'none',
		border: 'none',
		'border-radius': '4px',
		padding: '4px 0',
	},
	[`${CSS.symbolButton}:hover`]: { background: '#d2d2d2' },
	// Fixed-width slots flank the swatch symmetrically so it shares the preset cells' centerline.
	[CSS.symbolSlot]: {
		flex: '0 0 16px',
		width: '16px',
		display: 'flex',
		'align-items': 'center',
		'justify-content': 'center',
	},
	[CSS.symbolEdit]: { 'font-size': '14px', color: DefaultTheme.font.color.supporting },
	[CSS.visible]: { width: COL.visible, display: 'flex', 'align-items': 'center', 'justify-content': 'center' },
	// Visible = accent orange, matching the customizer's active-chip accent; hidden falls back to the button's default grey.
	[CSS.visibleIcon]: { color: DefaultTheme.color.accent },
	[CSS.actions]: { width: COL.actions, display: 'flex', 'align-items': 'center', 'justify-content': 'center' },
	[CSS.hidden]: { opacity: 0.45 },
	[CSS.ghost]: { opacity: 0.55 },
	[CSS.error]: {
		color: DefaultTheme.font.color.error,
		'font-size': '12px',
		padding: `0 0 ${DefaultTheme.space(1)} ${DefaultTheme.space(1)}`,
	},
	[CSS.disabled]: { opacity: 0.5 },
});

export class FormFieldEditorAssetType extends FormFieldEditor<FormModelFieldAssetType> {
	/** A single shared customizer popover; opened against whichever row's icon is clicked. */
	private readonly symbolPopover = new AnchoredPopover({ width: POPOVER_WIDTH });

	/** Section collapse flags. Observable: the app repaints via a MobX autorun, not auto-redraw. */
	@observable private isPresetsCollapsed = false;
	@observable private isCustomsCollapsed = false;

	constructor(designer: FormDesigner, field: FormModelFieldAssetType) {
		super(designer, field);
		makeObservable(this);
	}

	@action.bound private togglePresets() {
		this.isPresetsCollapsed = !this.isPresetsCollapsed;
	}

	@action.bound private toggleCustoms() {
		this.isCustomsCollapsed = !this.isCustomsCollapsed;
	}

	override destroy() {
		this.symbolPopover.close();
		super.destroy();
	}

	override viewInfoRW(): MithrilVNode {
		// Register the field-level validity gate so the tree node goes red when every type is hidden.
		this.visibilityState();
		// Without the asset-type token an existing field stays visible but read-only (no add row, all controls inert).
		const disabled = !Services.Token.has(IkeToken.FEATURE_ASSET_TYPE);
		// Partition by isPresetOption rather than assuming presets are contiguous (a custom row duplicating a
		// preset title renders preset-like — accepted edge).
		const presetIndices: number[] = [];
		const customIndices: number[] = [];
		this.field.assetTypes.forEach((_assetType, index) => {
			(this.field.isPresetOption(index) ? presetIndices : customIndices).push(index);
		});

		const customRows = customIndices.map((index) => this.viewRow(index));
		// Editing the trailing row — its title or its icon/color — is what adds it (like a select list).
		if (!disabled) {
			customRows.push(this.viewPlaceholderRow());
		}

		const body = m(
			'div',
			{
				className: classNames(CSS.container, { [CSS.disabled]: disabled }),
				// One lock for the whole subtree, rather than threading `disabled` through every input/button.
				style: disabled ? { pointerEvents: 'none' } : undefined,
			},
			[
				// Keep the popover a stable sibling of the row list; if it shared the list's children array its
				// open-state [backdrop, panel] nodes would interleave with row diffing and corrupt the placeholder.
				m('div', { className: CSS.rows }, [
					// Presets are visibility-toggle-only.
					SectionHeader.view({
						title: Translate(T.FIELD_ASSET_TYPE_DEFAULT_TYPES, { count: presetIndices.length }),
						collapsed: this.isPresetsCollapsed,
						onToggle: this.togglePresets,
					}),
					// Preset rows hold no input states, so collapsing can simply drop them.
					m(
						'div',
						{ className: CSS.section },
						this.isPresetsCollapsed
							? []
							: [this.viewColumnHeader(), ...presetIndices.map((index) => this.viewRow(index))]
					),
					// Customs trail the presets. Null keeps the children array a fixed shape (safe; only growing lists corrupt the diff).
					customRows.length === 0
						? null
						: SectionHeader.view({
								// Counts only real custom rows, not the trailing add placeholder.
								title: Translate(T.FIELD_ASSET_TYPE_CUSTOM_TYPES, { count: customIndices.length }),
								collapsed: this.isCustomsCollapsed,
								onToggle: this.toggleCustoms,
							}),
					m(
						'div',
						{ className: CSS.section },
						customRows.length === 0 || this.isCustomsCollapsed ? [] : [this.viewColumnHeader(), ...customRows]
					),
				]),
				// Stable wrapper: the popover fragment must never sit next to a conditionally-null sibling (skipped onRemove).
				m('div', {}, this.viewVisibilityError()),
				this.symbolPopover.view(),
			]
		);
		return disabled
			? ProFeatureTooltip.view({ stretch: true, message: Translate(T.FORM_OPTION_PRO_ONLY_TITLE), children: body })
			: body;
	}

	private viewColumnHeader(): MithrilVNode {
		return m('div', { className: CSS.header }, [
			m('div', { className: CSS.text }, Translate(T.FIELD_ASSET_TYPE_TYPE)),
			m('div', { className: CSS.symbol }, Translate(T.FIELD_ASSET_TYPE_ICON)),
			m('div', { className: CSS.visible }, Translate(T.FIELD_LINE_VISIBLE)),
			m('div', { className: CSS.actions }),
		]);
	}

	private viewRow(index: number): MithrilVNode {
		const assetType = this.field.assetTypes[index];
		// A row is a fixed preset iff it is the first occurrence of a default-library title. A later row that
		// duplicates a preset title is a custom row (kept editable + flagged), not a locked preset.
		const isCustom = !this.field.isPresetOption(index);

		const row = m('div', { className: assetType.visible ? CSS.row : `${CSS.row} ${CSS.hidden}` }, [
			m(
				'div',
				{ className: CSS.text },
				isCustom
					? Input.view(this.rowTitleState(index, false))
					: m('div', { className: CSS.textLabel }, assetType.title)
			),
			// Presets keep a fixed icon/title, but their color stays editable aside from Collection (default)
			m(
				'div',
				{ className: CSS.symbol },
				this.field.isColorEditable(index)
					? this.viewSymbolButton(index, { ...assetType, rotate: this.field.iconRotation(index) })
					: AssetTypeSwatch.view({ ...assetType, rotate: this.field.iconRotation(index) })
			),
			m(
				'div',
				{ className: CSS.visible },
				this.field.isVisibilityEditable(index)
					? ImageButton.view(assetType.visible ? MaterialIcon.VISIBILITY : MaterialIcon.VISIBILITY_OFF, {
							title: T.FIELD_LINE_VISIBLE,
							className: assetType.visible ? CSS.visibleIcon : undefined,
							onclick: () => this.field.Mutator.toggleVisibility(index),
						})
					: Icon.view(MaterialIcon.VISIBILITY, {
							className: CSS.visibleIcon,
							title: Translate(T.FIELD_ASSET_TYPE_VISIBLE_LOCKED),
						})
			),
			m(
				'div',
				{ className: CSS.actions },
				isCustom ? ImageButton.view(MaterialIcon.DELETE, { onclick: () => this.removeRow(index) }) : null
			),
		]);

		// One group per row so each row's inline error sits directly beneath it (above the divider).
		return m('div', { className: CSS.rowGroup }, [row, this.viewRowError(index)]);
	}

	/**
	 * The trailing add row. Keyed at `assetTypes.length`, it mirrors the custom-row structure so that the
	 * first edit promotes it in place: typing a title or picking an icon/color appends the row (see
	 * `commitTitle`/`applySymbol`), the shared input state keeps focus, and a fresh placeholder appears below.
	 */
	private viewPlaceholderRow(): MithrilVNode {
		const index = this.field.assetTypes.length;
		const row = m('div', { className: CSS.row }, [
			m('div', { className: `${CSS.text} ${CSS.ghost}` }, Input.view(this.rowTitleState(index, true))),
			m(
				'div',
				{ className: `${CSS.symbol} ${CSS.ghost}` },
				this.viewSymbolButton(index, { icon: '', color: DEFAULT_ASSET_TYPE_COLOR })
			),
			// No visibility toggle / delete until the row exists.
			m('div', { className: CSS.visible }),
			m('div', { className: CSS.actions }),
		]);
		// Same group shape as a real row so the first edit promotes it in place (DOM + focus carry over).
		return m('div', { className: CSS.rowGroup }, [row, null]);
	}

	private viewSymbolButton(index: number, symbol: { icon: string; color: string; rotate?: number }): MithrilVNode {
		return m(
			'button',
			{
				type: 'button',
				className: CSS.symbolButton,
				title: this.symbolButtonTitle(index),
				onclick: (event: MouseEvent) => this.openSymbolCustomizer(event, index),
			},
			[
				// Empty spacer mirrors the pencil slot so the swatch stays on the shared column centerline.
				m('span', { className: CSS.symbolSlot }),
				AssetTypeSwatch.view(symbol),
				m('span', { className: CSS.symbolSlot }, Icon.view(MaterialIcon.EDIT, { className: CSS.symbolEdit })),
			]
		);
	}

	/** Presets only expose a color picker (their icon is fixed), so the button/popover wording differs. */
	private customizeKey(index: number): T {
		return this.field.isPresetOption(index) ? T.FIELD_ASSET_TYPE_CUSTOMIZE_COLOR : T.FIELD_ASSET_TYPE_CUSTOMIZE_ICON;
	}

	private symbolButtonTitle(index: number): string {
		const title = this.field.assetTypes[index]?.title;
		return title ? Translate(this.customizeKey(index), { title }) : Translate(T.FIELD_ASSET_TYPE_ADD_CUSTOM);
	}

	/** Same heading, but with the asset-type name in its own ellipsizing segment so a long name never hides the close button. */
	private symbolPopoverTitle(index: number): MChild | MChild[] {
		const title = this.field.assetTypes[index]?.title;
		if (!title) {
			return Translate(T.FIELD_ASSET_TYPE_ADD_CUSTOM);
		}
		const [prefix = '', suffix = ''] = Translate(this.customizeKey(index), { title: NAME_SLOT }).split(NAME_SLOT);
		return [
			m('span', { className: PopoverCSS.titleFixed }, prefix),
			m('span', { className: PopoverCSS.titleName }, title),
			m('span', { className: PopoverCSS.titleFixed }, suffix),
		];
	}

	private openSymbolCustomizer(event: MouseEvent, index: number) {
		const anchor = event.currentTarget as HTMLElement;
		this.symbolPopover.open(anchor, {
			title: () => this.symbolPopoverTitle(index),
			content: () => this.viewSymbolCustomizer(index),
		});
	}

	private viewSymbolCustomizer(index: number): MithrilVNode {
		const assetType = this.field.assetTypes[index];
		// Preserve the OTHER half from the stored row (or the defaults for the not-yet-added placeholder) so a
		// pick never silently rewrites the other property. Live apply; the form's Save/Discard governs.
		const currentIcon = assetType?.icon ?? '';
		const currentColor = assetType?.color ?? DEFAULT_ASSET_TYPE_COLOR;
		// Presets keep a fixed icon — only their color is editable, so the icon grid is hidden and a pick
		// never touches the icon (setColor), unlike a custom row's combined icon+color apply.
		const isPreset = this.field.isPresetOption(index);
		return AssetTypeCustomizer.view({
			icon: currentIcon,
			color: currentColor,
			icons: CUSTOM_ASSET_TYPE_ICONS,
			palette: ASSET_TYPE_COLOR_PALETTE,
			iconEditable: !isPreset,
			rotate: this.field.iconRotation(index),
			onPickIcon: (icon) => this.applySymbol(index, icon, currentColor),
			onPickColor: (color) =>
				isPreset ? this.field.Mutator.setColor(index, color) : this.applySymbol(index, currentIcon, color),
		});
	}

	private viewRowError(index: number): MithrilVNode {
		const assetType = this.field.assetTypes[index];
		// Genuine presets always have a valid library title/icon; the placeholder isn't a real row yet. A custom row
		// that duplicates a preset title is NOT a preset here, so its duplicate error must still show.
		if (assetType == null || this.field.isPresetOption(index)) {
			return null;
		}
		// Registers the gate so a missing icon also turns the tree node red, mirroring rowTitleState below.
		this.rowIconState(index);
		const title = assetType.title.trim();
		let message: T | null = null;
		if (title.length === 0) {
			message = T.FIELD_ASSET_TYPE_TYPE_REQUIRED;
		} else if (assetType.icon.trim().length === 0) {
			message = T.FIELD_ASSET_TYPE_ICON_REQUIRED;
		} else if (this.field.hasDuplicateTitle(index, title)) {
			message = T.FIELD_ASSET_TYPE_TYPE_UNIQUE;
		}
		return message == null ? null : m('div', { className: CSS.error }, Translate(message));
	}

	/** A field with every type hidden offers nothing to pick — invalid (collected asset types reference a visible type). */
	private hasNoVisibleType(): boolean {
		return this.field.visibleAssetTypes.length === 0;
	}

	private viewVisibilityError(): MithrilVNode {
		return this.hasNoVisibleType()
			? m('div', { className: CSS.error }, Translate(T.FIELD_ASSET_TYPE_VISIBLE_REQUIRED))
			: null;
	}

	/**
	 * Field-level validity gate, mirroring the per-row title states: its error feeds `state.isValid`, which the
	 * tree view reads to turn this field's node red. The visible message is `viewVisibilityError`, so the state's
	 * own error is suppressed. Created lazily (and re-created after `clearStates`) so the reaction stays bound.
	 */
	private visibilityState(): InputState<string> {
		const key = 'visibility';
		let state = this.state.get(key);
		if (state == null) {
			state = this.state.child(T.FIELD_ASSET_TYPE_TYPE, key);
			state.isErrorShown = false;
			state.reaction(
				() => this.hasNoVisibleType(),
				(invalid) => state.setError(invalid ? Translate(T.FIELD_ASSET_TYPE_VISIBLE_REQUIRED) : ''),
				{ fireImmediately: true }
			);
		}
		return state;
	}

	private commitTitle(index: number, title: string) {
		if (index >= this.field.assetTypes.length) {
			this.field.Mutator.option({
				icon: '',
				color: DEFAULT_ASSET_TYPE_COLOR,
				title,
				visible: true,
			});
		} else {
			this.field.Mutator.setTitle(index, title);
		}
	}

	private applySymbol(index: number, icon: string, color: string) {
		if (index >= this.field.assetTypes.length) {
			this.field.Mutator.option({ icon, color, title: '', visible: true });
		} else {
			this.field.Mutator.setIcon(index, icon, color);
		}
	}

	private removeRow(index: number) {
		this.field.Mutator.remove(index);
		// Row indices shift, so rebuild the per-row input states.
		this.state.clearStates();
	}

	/**
	 * One title input state per row, keyed by index so the placeholder (`index === assetTypes.length`) becomes
	 * the new row's state on the next render — value and focus carry over. `isRequired` is recomputed each
	 * render: false while a placeholder (so it never blocks save), true once it is a real row.
	 * React-port note: index-as-key bleeds row state on reorder/delete; the port needs a stable per-row key
	 * (asset types carry no id today — title is their identity).
	 */
	private rowTitleState(index: number, isPlaceholder: boolean): InputState<string> {
		const key = `title:${index}`;
		let state = this.state.get(key);
		if (state == null) {
			state = this.state.child(T.FIELD_ASSET_TYPE_TYPE, key);
			// Suppress the input's own error; the inline `viewRowError` beneath the row is the visible feedback.
			state.isErrorShown = false;
			state.onValue((val: string) => this.commitTitle(index, val ?? ''));
			// Don't seed the placeholder's value — that would fire onValue and append an empty row on render.
			if (!isPlaceholder) {
				state.setValue(this.field.assetTypes[index]?.title ?? '');
			}
			state.reaction(
				() => this.titleError(index),
				(error) => state.setError(error),
				{ fireImmediately: true }
			);
		}
		state.isRequired = !isPlaceholder;
		state.setPlaceholder(isPlaceholder ? Translate(T.FIELD_ASSET_TYPE_TYPE_ADD) : '');
		return state;
	}

	/**
	 * Drives the row input's `error` (and thus save gating): a duplicate (non-empty) title invalidates the
	 * row. Empties are gated by `isRequired`. The visible message is the inline `viewRowError` beneath the row.
	 */
	private titleError(index: number): string {
		const current = this.field.assetTypes[index];
		const title = current?.title.trim() ?? '';
		if (title.length === 0) {
			return '';
		}
		return this.field.hasDuplicateTitle(index, title) ? Translate(T.FIELD_ASSET_TYPE_TYPE_UNIQUE) : '';
	}

	/**
	 * Field-level gate for a custom row's icon, mirroring `rowTitleState`: a fresh custom row starts with no
	 * icon (the designer must pick one), and its error feeds `state.isValid` so the tree node goes red until
	 * one is chosen. The visible message is the inline `viewRowError` beneath the row, so its own error is suppressed.
	 */
	private rowIconState(index: number): InputState<string> {
		const key = `icon:${index}`;
		let state = this.state.get(key);
		if (state == null) {
			state = this.state.child(T.FIELD_ASSET_TYPE_ICON, key);
			state.isErrorShown = false;
			state.reaction(
				() => this.iconError(index),
				(error) => state.setError(error),
				{ fireImmediately: true }
			);
		}
		return state;
	}

	private iconError(index: number): string {
		const current = this.field.assetTypes[index];
		return current != null && current.icon.trim().length === 0 ? Translate(T.FIELD_ASSET_TYPE_ICON_REQUIRED) : '';
	}
}
